import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { Cloud, DatabaseBackup, Globe2, HardDrive, Mail, Server, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import Reveal from "@/components/Reveal";
import { api, usd } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useLang } from "@/i18n";

const ICONS = [Globe2, Server, Mail, DatabaseBackup];
const PLAN_ICONS = [Globe2, Cloud, Server];

export default function CloudHosting() {
  const { t } = useLang();
  const { add, setOpen } = useCart();
  const [plans, setPlans] = useState([]);
  const features = t("cloud_features");
  const location = useLocation();

  useEffect(() => {
    if (location.hash === "#pricing") {
      setTimeout(() => {
        document.getElementById("cloud-pricing")?.scrollIntoView({ behavior: "smooth" });
      }, 400);
    }
  }, [location.hash]);

  useEffect(() => {
    api.get("/products", { params: { category: "Cloud Hosting" } })
      .then((r) => setPlans(r.data))
      .catch(() => setPlans([]));
  }, []);

  const orderPlan = (p) => {
    add(p);
    setOpen(true);
    toast.success(`${p.name} — ${t("added_to_cart")}`);
  };

  return (
    <main data-testid="cloud-page" className="bg-[#0B1120] min-h-screen pt-32 pb-24 px-6 md:px-12 lg:px-24">
      <Reveal>
        <div className="flex items-center gap-3 mb-4">
          <Cloud size={20} className="text-[#FFC61A]" />
          <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24]">{t("cloud_label")}</p>
        </div>
        <h1 className="font-display font-semibold text-white text-4xl sm:text-5xl lg:text-6xl tracking-tight mb-4 max-w-3xl">
          {t("cloud_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
        </h1>
        <p className="text-slate-400 text-sm lg:text-base mb-12 max-w-2xl">{t("cloud_sub")}</p>
      </Reveal>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-16 max-w-6xl">
        {(Array.isArray(features) ? features : []).map((f, i) => {
          const Icon = ICONS[i % ICONS.length];
          return (
            <Reveal key={f[0]} delay={i * 0.08}>
              <div
                data-testid={`cloud-feature-${i}`}
                className="group h-full bg-[#0F172A] border border-white/10 hover:border-[#ED1C24]/50 p-7 transition-colors duration-300"
              >
                <div className="w-11 h-11 border border-[#ED1C24]/40 flex items-center justify-center mb-5 group-hover:bg-[#ED1C24] transition-colors duration-300">
                  <Icon size={19} className="text-[#ED1C24] group-hover:text-white transition-colors duration-300" />
                </div>
                <h3 className="font-display font-semibold text-white text-lg mb-2">{f[0]}</h3>
                <p className="text-xs text-slate-400 leading-relaxed">{f[1]}</p>
              </div>
            </Reveal>
          );
        })}
      </div>

      {plans.length > 0 && (
        <Reveal delay={0.1}>
          <div id="cloud-pricing" data-testid="cloud-pricing" className="max-w-6xl mb-16">
            <p className="text-xs tracking-[0.25em] uppercase text-[#ED1C24] mb-3">{t("cloud_pricing_label")}</p>
            <h2 className="font-display font-semibold text-white text-2xl sm:text-3xl tracking-tight mb-8">
              {t("cloud_pricing_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
            </h2>
            <div className="grid sm:grid-cols-3 gap-5">
              {plans.map((p, i) => {
                const Icon = PLAN_ICONS[i % PLAN_ICONS.length];
                const perks = p.description.split("·").map((s) => s.trim()).filter(Boolean);
                return (
                  <div
                    key={p.id}
                    data-testid={`cloud-plan-${i}`}
                    className={`group bg-[#0F172A] border p-8 flex flex-col transition-colors duration-300 ${
                      i === 1 ? "border-[#ED1C24]/60" : "border-white/10 hover:border-[#ED1C24]/40"
                    }`}
                  >
                    <Icon size={22} className="text-[#FFC61A] mb-4" />
                    <h3 className="font-display font-semibold text-white text-lg mb-1">{p.name.replace(" (per year)", "")}</h3>
                    <p className="mb-5">
                      <span className="font-display text-3xl text-[#FFC61A]">{usd(p.price)}</span>
                      <span className="text-xs text-slate-500"> {t("cloud_per_year")}</span>
                    </p>
                    <ul className="space-y-2 mb-8 flex-1">
                      {perks.map((perk) => (
                        <li key={perk} className="text-xs text-slate-400 flex gap-2">
                          <span className="text-[#ED1C24]">—</span> {perk}
                        </li>
                      ))}
                    </ul>
                    <button
                      data-testid={`cloud-order-${i}`}
                      onClick={() => orderPlan(p)}
                      className="w-full inline-flex items-center justify-center gap-2 bg-[#ED1C24] hover:bg-[#C81016] text-white text-sm font-semibold py-3.5 transition-colors duration-300"
                    >
                      <ShoppingCart size={15} /> {t("cloud_order_btn")}
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </Reveal>
      )}

      <Reveal delay={0.15}>
        <div data-testid="cloud-cta" className="max-w-6xl border border-white/10 bg-gradient-to-br from-[#0F172A] to-[#111B33] p-10 lg:p-14 flex flex-wrap items-center justify-between gap-8">
          <div className="max-w-xl">
            <HardDrive size={22} className="text-[#FFC61A] mb-4" />
            <h2 className="font-display font-semibold text-white text-2xl sm:text-3xl tracking-tight mb-3">
              {t("cloud_cta_title").replace(".", "")}<span className="text-[#FFC61A]">.</span>
            </h2>
            <p className="text-slate-400 text-sm leading-relaxed">{t("cloud_cta_sub")}</p>
          </div>
          <a
            data-testid="cloud-portal-link"
            href="https://cloud.kinos.tl"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block bg-[#ED1C24] hover:bg-[#C81016] text-white font-semibold px-10 py-4 transition-all duration-300 hover:shadow-[0_0_32px_rgba(237,28,36,0.45)]"
          >
            {t("cloud_cta_btn")} →
          </a>
        </div>
      </Reveal>
    </main>
  );
}
